import { useCallback, useState } from "react";

export default function useUnsavedChanges({
  onClose,
  isDirtyFn,
  onSave,
  activeTab,
  setTab,
  guardedTab,
}) {
  const [pendingAction, setPendingAction] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const tabToGuard = guardedTab || activeTab;
  const hasTabFunctionality = setTab !== undefined;

  const attemptClose = useCallback(() => {
    const isDirty = isDirtyFn?.() ?? false;

    const shouldGuard = hasTabFunctionality ? activeTab === tabToGuard && isDirty : isDirty;

    if (shouldGuard) {
      setPendingAction({ type: "close" });
      setShowDialog(true);
      return;
    }
    onClose();
  }, [activeTab, tabToGuard, isDirtyFn, onClose, hasTabFunctionality]);

  const attemptChangeTab = useCallback(
    nextTab => {
      if (!hasTabFunctionality) {
        return;
      }

      const isDirty = isDirtyFn?.() ?? false;
      if (activeTab === tabToGuard && nextTab !== tabToGuard && isDirty) {
        setPendingAction({ type: "tab", value: nextTab });
        setShowDialog(true);
        return;
      }
      setTab(nextTab);
    },
    [activeTab, tabToGuard, isDirtyFn, setTab, hasTabFunctionality]
  );

  const confirmDiscard = useCallback(() => {
    if (pendingAction?.type === "close") {
      setShowDialog(false);
      setPendingAction(null);
      onClose();
      return;
    }
    if (pendingAction?.type === "tab") {
      const next = pendingAction.value;
      setShowDialog(false);
      setPendingAction(null);
      if (setTab) {
        setTab(next);
      }
      return;
    }
    setShowDialog(false);
    setPendingAction(null);
  }, [onClose, pendingAction, setTab]);

  const cancelDiscard = useCallback(() => {
    setShowDialog(false);
    setPendingAction(null);
  }, []);

  const confirmSave = useCallback(async () => {
    setSaving(true);
    try {
      if (typeof onSave === "function") {
        await onSave();
      }
      const action = pendingAction;
      setShowDialog(false);
      setPendingAction(null);
      if (action?.type === "close") {
        onClose();
      } else if (action?.type === "tab" && setTab) {
        setTab(action.value);
      }
    } catch (error) {
      // A failed save must not close or switch away from the unsaved edits.
      // Drop the prompt so the user can see the error and retry or discard.
      console.error("Error saving unsaved changes:", error);
      setShowDialog(false);
      setPendingAction(null);
    } finally {
      setSaving(false);
    }
  }, [onSave, onClose, pendingAction, setTab]);

  return {
    attemptClose,
    attemptChangeTab,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving,
  };
}
